import { useEffect, useState } from 'react';
import { comparisonActions, useCOACComparisonPlot } from '@/stores/comparisonStore';
import COACComparisonForm from './COACComparisonForm';
import StackedBarChart from '@/components/charts/StackedBarChart';
import COACComparisonSummary from './COACComparisonSummary';
import AbatementPotentialPlot from './AbatementPotentialPlot';
import AnalysisContainer from '@/components/core/AnalysisContainer';
import AnalysisBox from '@/components/core/AnalysisBox';
import Tooltip from '@/components/core/Tooltip';
import { isEmpty } from '@/utils/miscUtils';

const MACC_TOOLTIP_TEXT = `Each bar represents a single intervention compared **independently** against the baseline — bars are not cumulative or successive. Implementing intervention A does not carry over into the starting point for intervention B.

**Bar height** = Cost of Avoided Carbon (COAC) in $/ton CO₂e. **Bar width** = annual CO₂e avoided at each case's modelled production volume (wider = more abatement potential). Bars are sorted cheapest to most expensive.

**COAC ($/ton CO₂e)** = (Levelized cost − Baseline cost) ÷ (Baseline emissions intensity − Case emissions intensity)

**When to use:** best suited for comparing multiple interventions each evaluated against the same fixed reference pathway. Not meaningful when cases represent pathways modelled at materially different production scales.`;

const COACComparisonPlot = () => {
  const plot = useCOACComparisonPlot();
  const updatePlot = formData => comparisonActions.updatePlot('coac', formData);
  const [viewMode, setViewMode] = useState('coac');

  useEffect(() => {
    setViewMode('coac');
  }, [plot.data]);

  if (isEmpty(plot)) {
    return null;
  }

  if (plot.pending) {
    return (
      <AnalysisContainer title="Cost Of Avoided Carbon">
        <div className="ml-4 loading loading-dots loading-md"></div>
      </AnalysisContainer>
    );
  }

  const { results } = plot;
  const hasMACCData = results?.maccBars?.length > 0;

  const title = (
    <div className="flex items-center gap-3">
      <span>Cost Of Avoided Carbon</span>
      <div className="join font-normal">
        <button
          className={`join-item btn btn-xs ${viewMode === 'coac' ? 'btn-neutral text-white' : 'btn-ghost'}`}
          onClick={() => setViewMode('coac')}
        >
          Cost
        </button>
        <button
          className={`join-item btn btn-xs ${viewMode === 'macc' ? 'btn-neutral text-white' : 'btn-ghost'}`}
          onClick={() => setViewMode('macc')}
          disabled={!hasMACCData}
        >
          Abatement Potential
        </button>
      </div>
      <Tooltip label="Abatement Potential" text={MACC_TOOLTIP_TEXT} />
    </div>
  );

  return (
    <AnalysisContainer title={title}>
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-3">
          <AnalysisBox>
            <>
              {results && viewMode === 'coac' && (
                <StackedBarChart
                  rows={results.rows}
                  columns={results.columns}
                  yAxisLabel={`\n${results.yAxis}`}
                  labelsInCSV={true}
                />
              )}
              {results && viewMode === 'macc' && (
                <AbatementPotentialPlot
                  maccBars={results.maccBars}
                  yAxisLabel={results.yAxis}
                />
              )}
              {results && (
                <p className="mt-2 text-xs text-base-content/60">
                  <span className="font-medium">COAC ({results.yAxis})</span> = (Levelized cost − Baseline cost) ÷ (Baseline emissions intensity − Case emissions intensity).
                </p>
              )}
              <COACComparisonForm onSubmit={updatePlot} defaultValues={plot} />
            </>
          </AnalysisBox>
        </div>
        {results && results?.rows?.[0]?.length > 1 && (
          <AnalysisBox>
            <COACComparisonSummary
              errors={results.errors}
              minTotal={results.minTotal}
              maxTotal={results.maxTotal}
              unit={results.yAxis}
            />
          </AnalysisBox>
        )}
      </div>
    </AnalysisContainer>
  );
};

export default COACComparisonPlot;
